/**
 * Romanian UI copy for admin system_config keys and nested retrieval.weights fields.
 */

export type ConfigHelp = { short: string; long: string };

export const CONFIG_KEY_HELP: Record<string, ConfigHelp> = {
  'retrieval.weights': {
    short: 'Ponderi și praguri pentru căutare, reranking și escaladare.',
    long:
      'Setul de valori numerice folosit de serviciul de căutare al documentelor: combină vectori (embeddings), potriviri lexicale (cuvinte-cheie, titluri, etichete), similaritate trigram pe text, reranking pe fragmente și reguli de „escaladare” când rezultatele sunt slabe (ex. cere clarificare utilizatorului). Modificați incremental și testați în chat. Câmpuri `agentic_*`: planificator, temperaturi răspuns/validator, praguri încredere pentru ture suplimentare, override-uri context (0 = dezactivate).',
  },
  'maps.synonyms': {
    short: 'Mapări termen canonic → sinonime pentru normalizare la căutare.',
    long:
      'Fiecare cheie este un termen normalizat; valorile sunt liste de sinonime recunoscute în text. Ajută la potrivire când utilizatorul sau documentul folosesc variante diferite (ex. abreviere vs formă completă). Familiile „coarse” de tip document folosite la filtrare chat sunt derivate automat din clasificatorul `document_type` din pagina Clasificări.',
  },
  'prompts.intent.system': {
    short: 'Instrucțiuni sistem pentru extragerea intenției de căutare (JSON).',
    long:
      'Prompt de sistem pentru modelul care interpretează întrebarea utilizatorului și produce structura JSON (limită, ordine, tip document, etichete, specialitate, cuvinte-cheie, date, needs_full_text). Schimbările afectează direct calitatea filtrării documentelor. ' +
      'Important: numele subiectului/pacientului NU trebuie să apară în `keywords` sau `analytes` — subiectul este deja rezolvat upstream prin member_id, iar adăugarea numelui ca termen de căutare ar restrânge greșit retrieval-ul la documentele care conțin literal numele (cele mai multe documente sunt anonimizate).',
  },
  'prompts.intent.user': {
    short: 'Șablon mesaj utilizator pentru intenția de căutare.',
    long:
      'Conținutul trimis împreună cu contextul membrului pentru extragerea intenției. Folosește variabile $member_label, $user_message, $ui_language.',
  },
  'prompts.answer.system': {
    short: 'Rolul asistentului la răspunsul din chat pe baza documentelor.',
    long:
      'Definește tonul și constrângerile (fără diagnostic, citare din surse, limbă).',
  },
  'prompts.answer.user': {
    short: 'Șablon user: întrebare + context documente pentru răspuns.',
    long:
      'Include $member_name, $user_message, $ui_language, $context (fragmente document). Poate cere linia tehnică SOURCES_USED (eliminată din răspunsul afișat); sursele din UI se filtrează după documentele citate.',
  },
  'prompts.agent_planner.system': {
    short: 'Planificator agentic (JSON): reguli și schemă.',
    long: 'Instrucțiuni pentru modelul de planificare după prima rundă de retrieval; ieșire strict JSON.',
  },
  'prompts.agent_planner.user': {
    short: 'Planificator agentic: șablon user.',
    long:
      'Variabile: $member_label, $user_message, $retrieval_summary, $conversation, $focus_mode, $ui_language.',
  },
  'prompts.answer_agentic.system': {
    short: 'Răspuns chat mod analiză extinsă (sistem).',
    long: 'Ton și structură pentru răspunsuri mai detaliate, tot din documente; $ui_language, $focus_mode.',
  },
  'prompts.answer_agentic.user': {
    short: 'Răspuns chat mod analiză extinsă (user).',
    long:
      'Include $member_name, $user_message, $conversation, $focus_mode, $context, $ui_language; linie SOURCES_USED.',
  },
  'prompts.answer_validator.system': {
    short: 'Validator siguranță / grounding (sistem).',
    long: 'Cere JSON {is_safe, issues, fix_required} fără text suplimentar.',
  },
  'prompts.answer_validator.user': {
    short: 'Validator: întrebare + rezumat surse + draft.',
    long: 'Variabile $user_message, $retrieval_summary, $draft_answer, $ui_language.',
  },
  'prompts.summary.system': {
    short: 'Instrucțiuni pentru rezumate și analiză documente.',
    long:
      'Folosit la fluxuri de rezumat; stabilește reguli de siguranță și limbă ($ui_language).',
  },
  'prompts.timeline.system': {
    short: 'Instrucțiuni pentru cronologie (JSON structurat).',
    long:
      'Cronologia medicală este acum un document JSON validat strict (schema în `backend/app/schemas/timeline.py`). System-prompt-ul cere modelului să emită DOAR un obiect cu `events` + `highlights`; restul wrapper-ului (member_id, generated_at, total_events, date_range) este calculat de server. Câmpuri închise: ' +
      '`category` ∈ {lab, imaging, clinical, procedure, medication, diagnosis, vaccination, consultation, other}; ' +
      '`severity` ∈ {info, notable, important, critical}; ' +
      '`status` ∈ {normal, abnormal, improving, worsening, stable, unknown}; ' +
      '`date_precision` ∈ {day, month, year, approx}. ' +
      '`source_document_ids` se validează contra contextului (id-urile inexistente sunt eliminate). Fără variabile $ în acest text.',
  },
  'prompts.insights.system': {
    short: 'Instrucțiuni pentru insight-uri agregate.',
    long:
      'Folosit când se sintetizează pattern-uri sau observații din mai multe surse.',
  },
  'prompts.summary.single_user': {
    short: 'Prompt user pentru rezumat la un singur document.',
    long:
      'Primește textul documentului și cere rezumat structurat; limba prin $ui_language.',
  },
  'prompts.summary.map_chunk': {
    short: 'Rezumat pe fragment (map) dintr-un document mare.',
    long:
      'Folosit pe bucăți paralele; variabile $chunk_index, $num_chunks, $chunk, $ui_language.',
  },
  'prompts.summary.reduce_user': {
    short: 'Combină rezumate parțiale într-un rezumat final.',
    long:
      'Primește $combined (rezumate parțiale) și unifică; limba $ui_language.',
  },
  'prompts.timeline.user': {
    short: 'Șablon user pentru cronologie (JSON structurat).',
    long:
      'Variabile: $member_name, $documents_payload (lista de documente cu document_id-uri prefixate, folosite de model pentru `source_document_ids`). Răspunsul trebuie să fie un singur obiect JSON conform schemei din system prompt.',
  },
  'prompts.insights.user': {
    short: 'Șablon user pentru cerere insight-uri.',
    long:
      'Formulatează întrebarea utilizatorului și contextul pentru analiză.',
  },
  'prompts.classification.system': {
    short: 'Clasificare tip document / metadata (sistem).',
    long:
      'Șablon system pentru clasificare. Variabile disponibile (substituite la runtime din taxonomia configurată în Clasificări): ' +
      '$json_structure (scheletul JSON așteptat), $preferred_values (valorile preferate pe clasificator), $relations (regulile dacă-atunci între clasificatori). ' +
      'Folosește sintaxa Python string.Template ($nume). Butonul „Previzualizează randarea" arată exact ce primește LLM-ul.',
  },
  'prompts.classification.user': {
    short: 'Șablon user pentru clasificare.',
    long:
      'Șablon per-document trimis modelului împreună cu textul OCR. Variabile disponibile: ' +
      '$document (textul OCR / anonimizat al documentului). Folosește sintaxa Python string.Template ($document).',
  },
  'prompts.chunk_answer.system': {
    short: 'Răspuns pe fragment (chunk) — system.',
    long:
      'Folosit când întrebarea utilizatorului este aplicată pe fragmente recuperate (modul standard). Variabile: $member_name, $ui_language. ' +
      'Conține regula de „context de subiect”: subiectul este deja rezolvat upstream (member_id), iar TOATE fragmentele sunt deja scoped la el. Modelul NU trebuie să caute numele subiectului în text și NU trebuie să refuze răspunsul pentru că numele nu apare (documentele sunt frecvent anonimizate cu marcaje [NUME] / [PACIENT]). „Nu am informații” este valid DOAR când lista de fragmente este goală.',
  },
  'prompts.chunk_answer.user': {
    short: 'Răspuns pe fragment (chunk) — user.',
    long: 'Variabile: $user_message, $context (fragmentul), $ui_language.',
  },
  'prompts.agent_loop.system': {
    short: 'Buclă agent LangChain (system).',
    long:
      'Prompt de sistem pentru AgentExecutor: definește invariantul de autorizație ' +
      '(tool-urile aplică deja filtrul utilizatorului curent), regulile de replanificare ' +
      'la retrieval slab (cere clarificări în loc să halucineze), bugetul de 6 apeluri ' +
      'tool și contractul de output (SOURCES_USED: id1,id2,... pe ultima linie). ' +
      'Conține și regula de „context de subiect”: dosarul returnat de tool-uri este deja scoped ' +
      'la member_id rezolvat, deci modelul NU trebuie să caute numele subiectului în textul ' +
      'fragmentelor și NU trebuie să includă numele subiectului în query-urile de tool ' +
      '(numele este metadată de scope, nu termen de căutare). Răspunde „nu am informații” DOAR ' +
      'dacă tool-urile au returnat efectiv zero rezultate. Fără variabile $.',
  },
  'prompts.multiquery.system': {
    short: 'MultiQueryRetriever — generator paraphrases (system).',
    long:
      'Reformulează întrebarea în 3 variante pentru a îmbunătăți căutarea. Fiecare ' +
      'variantă pe rând nou, fără numerotare sau explicații. Înlocuiește prompt-ul ' +
      'englez implicit din LangChain cu o variantă RO tunată pentru sinonime medicale. ' +
      'Fără variabile $.',
  },
  'responses.escalation.clarification': {
    short: 'Mesaj când căutarea e slabă și e nevoie de clarificare.',
    long:
      'Afișat utilizatorului când scorurile de retrieval sunt sub praguri; poate cere detalii suplimentare.',
  },
  'responses.chat.empty_documents': {
    short: 'Răspuns când nu există documente pentru membru.',
    long:
      'Text scurt în chat când nu se găsesc documente relevante.',
  },
  'responses.chat.no_members': {
    short: 'Mesaj când nu există membri în cont.',
    long:
      'Ghidare când utilizatorul nu are pacienți asociați.',
  },
  'responses.chat.ask_which_member': {
    short: 'Invitație de a alege pacientul când lipsește contextul.',
    long:
      'Folosit când întrebarea necesită un membru dar nu e selectat unul clar.',
  },
  'responses.chat.ambiguous_members': {
    short: 'Când există mai mulți candidați de membri.',
    long:
      'Text pentru dezambiguizare (alegere clară a pacientului).',
  },
};

/** Per-field copy for retrieval.weights JSON object. */
export const RETRIEVAL_WEIGHT_FIELDS: Record<string, { label: string; short: string; long: string }> = {
  vector_weight: {
    label: 'Pondere vectori',
    short: 'Importanța similarității embedding față de interogare.',
    long:
      'Înmulțește scorul de similaritate cosine între vectorul întrebării și cel al documentului. Valori mai mari favorizează potrivirea semantică largă.',
  },
  keyword_hit_boost: {
    label: 'Amplificare potrivire cuvinte',
    short: 'Bonus când termenii din intenție apar în text.',
    long:
      'Mărește scorul când cuvintele-cheie extrase din intenție apar în conținutul documentului sau fragmentelor.',
  },
  title_keyword_boost: {
    label: 'Bonus titlu',
    short: 'Extra scor dacă termenii apar în titlul documentului.',
    long:
      'Utile pentru documente cu titluri descriptive (ex. tip raport).',
  },
  tag_match_boost: {
    label: 'Potrivire etichete',
    short: 'Bonus când etichetele documentului se aliniază cu intenția.',
    long:
      'Crește relevanța dacă tag-urile stocate pe document se potrivesc cu cele cerute în intenție.',
  },
  document_type_boost: {
    label: 'Tip document',
    short: 'Bonus pentru tipul de document cerut.',
    long:
      'Folosit când utilizatorul sau intenția specifică un tip (clasificare / etichetă umană).',
  },
  specialty_match_boost: {
    label: 'Specialitate / facet principal',
    short: 'Bonus aplicat documentelor care se potrivesc cu facetul principal din intenție.',
    long:
      'Întărește documentele aliniate cu specialitatea sau facetul principal extras din intenție (de exemplu „specialty”, „department” sau orice alt clasificator marcat ca primar).',
  },
  max_db_candidates: {
    label: 'Max. candidați din DB',
    short: 'Câte documente cel mult aduc din căutarea inițială.',
    long:
      'Limitează numărul de rânduri evaluate după filtre SQL înainte de reranking; protejează latența.',
  },
  max_docs_in_context: {
    label: 'Max. documente în context LLM',
    short: 'Câte documente trimit la model pentru răspuns.',
    long:
      'După selectare, doar primele N documente intră în $context pentru generarea răspunsului.',
  },
  max_markdown_chars_per_doc: {
    label: 'Caractere max. / document',
    short: 'Trunchiere text markdown per document în context.',
    long:
      'Evită depășirea ferestrei de context; fiecare document este tăiat la acest număr de caractere.',
  },
  max_context_chunk_chars: {
    label: 'Max. caractere fragment vector',
    short: 'Limită pentru fragmentul semantic (Qdrant) trimis la LLM.',
    long:
      'Răspunsul chat folosește fragmentul cel mai similar din indexul vectorial, nu tot PDF-ul. Acest prag trunchiază acel fragment.',
  },
  answer_keep_doc_within_vector_gap: {
    label: 'Prag similaritate (doc. multiple)',
    short: 'Păstrează al doilea document doar dacă sim. vector e aproape de primul.',
    long:
      'Dacă > 0: după sortare, elimină documentele al căror scor de similaritate vectorială este cu mai mult decât această valoare sub cel mai bun (ex. 0,08). Reduce cazurile cu mai multe rapoarte foarte similare când doar primul este cu adevărat relevant. 0 = dezactivat.',
  },
  max_docs_narrow_intent: {
    label: 'Max. doc. intenție „largă”',
    short: 'Câte documente la needs_full_text false.',
    long:
      'Când intenția marchează needs_full_text false (întrebări de tip listă / supraveghere), limitează câte documente intră în contextul pentru răspuns.',
  },
  recency_factor: {
    label: 'Factor recență',
    short: 'Cât de mult favorizează documentele mai noi.',
    long:
      'Între 0 și 1 tipic: ajustează scorul în funcție de data documentului (mai nou = bonus mai mare).',
  },
  small_pool_max_candidates: {
    label: 'Max. candidați (pool mic)',
    short: 'Scanare vectori când membrul are puține documente.',
    long:
      'Adaptiv: dacă membrul are sub pragul „large_pool”, se caută în mai puține candidați pentru viteză.',
  },
  large_pool_threshold: {
    label: 'Prag pool mare',
    short: 'Număr documente de la care se consideră „pool mare”.',
    long:
      'Dacă numărul de documente ale membrului depășește acest prag, se folosesc setările de pool mare (mai mulți candidați vectori).',
  },
  rerank_top_pool: {
    label: 'Pool reranking',
    short: 'Câte rezultate intră în a doua trecere (fragmente).',
    long:
      'După scor inițial, se iau primele N documente și se re-evaluează folosind potrivire pe fragmente (chunk) cu cuvinte-cheie.',
  },
  rerank_chunk_keyword_boost: {
    label: 'Amplificare chunk la rerank',
    short: 'Bonus când cuvintele apar în fragmentul vectorului.',
    long:
      'În pasul de reranking, mărește scorul dacă termenii din intenție apar în textul chunk-ului folosit la embedding.',
  },
  trgm_similarity_boost: {
    label: 'Similaritate trigram',
    short: 'Bonus pentru potrivire fuzzy pe text integral (pg_trgm).',
    long:
      'Folosește similaritatea PostgreSQL între ultimul text indexat al documentului și „acul” de căutare; util pentru variante de scriere.',
  },
  escalation_min_total_score: {
    label: 'Prag scor minim (escaladare)',
    short: 'Sub acest scor, rezultatele sunt considerate slabe.',
    long:
      'Dacă cel mai bun scor agregat este sub această valoare, se poate declanșa fluxul de clarificare (împreună cu ceilalți parametri de escaladare).',
  },
  escalation_max_top_vector_sim: {
    label: 'Max. similaritate vector top',
    short: 'Dacă top-ul vector e sub acest prag, escaladare.',
    long:
      'Chiar dacă există candidați, dacă similaritatea vectorială a celui mai bun rezultat este sub acest prag (ex. 0,22), se consideră că lipsește potrivirea semantică.',
  },
  escalation_min_candidate_pool: {
    label: 'Min. candidați pentru escaladare',
    short: 'Nu cere clarificare dacă sunt prea puține documente.',
    long:
      'Evită mesaje de clarificare când baza e mică (nu are sens să întrebi „care document?” dacă există foarte puține).',
  },
  escalation_only_when_needs_full_text: {
    label: 'Escaladare doar la nevoie text complet',
    short: '1 = escaladare mai ales când e nevoie de detaliu în text.',
    long:
      'Dacă valoarea ≥ 0,5, clarificarea pentru retrieval slab se aplică preferențial când intenția cere analiză detaliată (needs_full_text), nu pentru întrebări generice de tip listă.',
  },
  citation_fallback_min_score_ratio: {
    label: 'Surse: prag scor relativ',
    short: 'Dacă modelul nu citează document_id, păstrează doar doc. cu scor ≥ acest raport față de top.',
    long:
      'Lista „Documente relevante” nu mai include toate documentele trimise la LLM, ci doar cele citate (sau, dacă lipsește orice citare, cele apropiate de scorul maxim). Raport implicit 0,88 = exclude documentele cu scor mult mai mic decât primul.',
  },
  citation_fallback_min_score_below_top: {
    label: 'Surse: prag scor absolut sub top',
    short: 'Alternativă la raport: păstrează doc. cu scor ≥ (top − această valoare).',
    long:
      'Dacă este > 0, înlocuiește regula bazată pe `citation_fallback_min_score_ratio`: se păstrează documentele al căror scor de retrieval nu este cu mai mult decât această valoare sub scorul maxim. 0 = folosește doar raportul.',
  },
};

export function getConfigKeyHelp(key: string): ConfigHelp {
  const direct = CONFIG_KEY_HELP[key];
  if (direct) return direct;
  if (key.startsWith('_prepare_')) {
    return {
      short: 'Șablon intern pentru pregătirea mesajelor către LLM (chat).',
      long:
        'Cheie tehnică din fluxul de chat; conținutul este combinat cu contextul membrului și documentelor. Modificați doar dacă cunoașteți contractul din cod.',
    };
  }
  if (key.startsWith('responses.')) {
    return {
      short: 'Mesaj scurt afișat utilizatorului în chat (nu este prompt LLM).',
      long:
        'Categoria „responses” conține texte de interfață și clarificări afișate direct utilizatorului. Fără variabile $ — editați textul ca atare.',
    };
  }
  return {
    short: 'Intrare de configurare aplicație.',
    long:
      'Această cheie controlează comportamentul aplicației. Consultați JSON-ul sau documentația tehnică înainte de modificări majore.',
  };
}

/**
 * Friendly Romanian labels per nested-key middle segment.
 *
 * Keys in `system_config` follow the `<category>.<group>.<leaf>` shape
 * (e.g. `prompts.classification.system`). The AdminConfig UI groups
 * every row that shares the same `<group>` into a single card; this
 * map provides the human label shown in the card header. Fallback for
 * unknown groups: derive a title from the raw segment.
 *
 * Whenever a new `prompts.*` / `responses.*` group is introduced in the
 * seed list, add a matching entry here so the card gets a friendly
 * name out of the box.
 */
export const CONFIG_GROUP_LABELS: Record<string, string> = {
  intent: 'Intenție căutare',
  answer: 'Răspuns chat (basic)',
  answer_agentic: 'Răspuns chat (agentic)',
  agent_planner: 'Planificator chat agentic',
  answer_validator: 'Validator răspuns',
  summary: 'Rezumat document',
  timeline: 'Cronologie membru',
  insights: 'Insight-uri membru',
  classification: 'Clasificare document',
  escalation: 'Mesaje de escaladare',
  chat: 'Mesaje chat (member resolver)',
  weights: 'Ponderi căutare',
  synonyms: 'Sinonime căutare',
  chunk_answer: 'Răspuns pe fragment (chunk)',
  agent_loop: 'Buclă agent LangChain',
  multiquery: 'MultiQuery — reformulări de căutare',
};

export function getConfigGroupLabel(group: string): string {
  return (
    CONFIG_GROUP_LABELS[group] ??
    group
      .split('_')
      .map((s) => (s ? s[0].toUpperCase() + s.slice(1) : s))
      .join(' ')
  );
}

export function getRetrievalWeightField(field: string) {
  return (
    RETRIEVAL_WEIGHT_FIELDS[field] ?? {
      label: field.replace(/_/g, ' '),
      short: 'Parametru numeric în pipeline-ul de căutare.',
      long:
        'Pondere sau prag folosit în calculul scorurilor sau în regulile de escaladare. Modificați cu pași mici și verificați comportamentul în chat.',
    }
  );
}
